/**
 * Pots and side pots.
 *
 * Built from what each player has put in over the WHOLE hand, not per street.
 * Doing it per street is the classic way to get this wrong: a player who is all-in
 * on the flop for less than the others is eligible for the flop money but not the
 * turn money, and only a whole-hand view of contributions gets that right.
 *
 * Folded players' chips stay in the pot — they are dead money and count toward every
 * pot they reached — but a folded player is never eligible to win any of it.
 */

export interface Pot {
  amount: number;
  /** Seats that can win this pot: still in the hand, and paid up to its level. */
  eligible: number[];
}

export interface PotSplit {
  pots: Pot[];
  /**
   * Chips handed back, per seat.
   *
   * A band of the pot that no live player can win is not dead money — it is money
   * nobody called, and it goes back to whoever put it in. The case is easy to miss and
   * the fuzz run found it: a seat all-in for less than the blinds, everyone else folds,
   * and the part of the blinds above the all-in belongs to a folded player. Skipping
   * that band, which is what the first version did, quietly destroyed those chips.
   */
  refunds: number[];
}

/**
 * Split total contributions into a main pot, however many side pots follow, and the
 * uncalled chips that go back to their owners.
 *
 * `contributed[i]` is everything seat i has put in this hand. `live` is the seats
 * that have not folded. Chips are conserved exactly: the pots plus the refunds sum to
 * the total put in.
 */
export function buildPots(contributed: number[], live: Set<number>): PotSplit {
  const levels = [...new Set(contributed.filter((c) => c > 0))].sort((a, b) => a - b);
  const pots: Pot[] = [];
  const refunds = new Array<number>(contributed.length).fill(0);
  let prev = 0;

  for (const level of levels) {
    let amount = 0;
    const eligible: number[] = [];
    const payers: { seat: number; paid: number }[] = [];
    for (let i = 0; i < contributed.length; i++) {
      if (contributed[i]! <= prev) continue;
      // Everyone who reached this level pays into it, folded or not.
      const paid = Math.min(contributed[i]!, level) - prev;
      amount += paid;
      payers.push({ seat: i, paid });
      if (live.has(i)) eligible.push(i);
    }
    prev = level;
    if (amount <= 0) continue;

    // Nobody left can win this band, so it was never called: give it back.
    if (eligible.length === 0) {
      for (const p of payers) refunds[p.seat]! += p.paid;
      continue;
    }

    /**
     * Merge with the previous pot when the same players can win it. Without this a
     * nine-handed hand produces a stack of one-chip "side pots" that are really one
     * pot, which is correct arithmetic and unreadable output.
     */
    const last = pots[pots.length - 1];
    if (last && sameSeats(last.eligible, eligible)) last.amount += amount;
    else pots.push({ amount, eligible });
  }
  return { pots, refunds };
}

/** The first of these seats going clockwise from the button. */
function firstClockwise(seats: number[], button: number, seatCount: number): number {
  for (let step = 1; step <= seatCount; step++) {
    const seat = (button + step) % seatCount;
    if (seats.includes(seat)) return seat;
  }
  return seats[0]!;
}

const sameSeats = (a: number[], b: number[]): boolean =>
  a.length === b.length && a.every((x, i) => x === b[i]);

/**
 * Award every pot and return each seat's winnings.
 *
 * `rank` is the seat's showdown strength, higher is better; seats not at showdown
 * should be absent from a pot's `eligible` list rather than given a low rank.
 *
 * Odd chips go to the first eligible seat clockwise from the button, which is the
 * standard rule and matters more than it sounds: without a fixed rule the same hand
 * settles differently depending on iteration order, and a replay stops reproducing.
 */
export function awardPots(
  pots: Pot[], rank: (seat: number) => number, seatCount: number, button: number,
  chipSize = 0
): number[] {
  const won = new Array(seatCount).fill(0);

  for (const pot of pots) {
    if (pot.eligible.length === 0) continue;
    let best = -Infinity;
    let winners: number[] = [];
    for (const s of pot.eligible) {
      const r = rank(s);
      if (r > best) { best = r; winners = [s]; }
      else if (r === best) winners.push(s);
    }
    if (winners.length === 1) { won[winners[0]!] += pot.amount; continue; }

    /**
     * Split a shared pot without creating or destroying chips.
     *
     * The first version rounded the pot to the nearest whole chip and then divided
     * THAT — so a pot of 178.3 in a game with 100-chips became two chips, each winner
     * took one, and twenty-one and a bit chips were invented out of nothing. The fuzz
     * run found it as "chips not conserved (-21.7)".
     *
     * The order below is what makes it exact: whole chips each, then the odd chips
     * clockwise from the button (the standard rule, and a fixed one so a replay
     * reproduces), then whatever sub-chip remainder is left to the first eligible seat
     * clockwise. That remainder should never exist in a well-formed game — it only
     * appears when the blinds or the ante are not multiples of the chip — but the
     * engine must not lose chips just because someone configured a table badly.
     */
    if (chipSize <= 0) {
      // Fractional chips are allowed (big-blind-denominated play): split exactly, and
      // give the last winner the residue so the parts sum to the whole.
      const share = pot.amount / winners.length;
      let handed = 0;
      for (let i = 0; i < winners.length - 1; i++) { won[winners[i]!] += share; handed += share; }
      won[winners[winners.length - 1]!] += pot.amount - handed;
      continue;
    }

    const units = Math.floor(pot.amount / chipSize + 1e-9);
    const each = Math.floor(units / winners.length);
    let odd = units - each * winners.length;
    let handed = 0;
    for (const w of winners) { won[w] += each * chipSize; handed += each * chipSize; }
    for (let step = 1; step <= seatCount && odd > 0; step++) {
      const seat = (button + step) % seatCount;
      if (winners.includes(seat)) { won[seat] += chipSize; handed += chipSize; odd--; }
    }
    const remainder = pot.amount - handed;
    if (Math.abs(remainder) > 1e-12) {
      const first = firstClockwise(winners, button, seatCount);
      won[first] += remainder;
    }
  }
  return won;
}
